<template>
  <DocumentLayout>
    <h1 class="my-10 text-center text-6xl font-bold">Privacy Policy</h1>
    <p>
      Welcome to https://athleticnext.com ("Site"), hosted by
      <strong>AthleticNext</strong> (" AthleticNext ", "we", "us" and/or "our").
      AthleticNext provides a cloud platform for video storage, sharing and
      analysing using this website, mobile applications, and APIs ("Services").
      In order to provide our Site and Services (collectively, "Platform"), we
      collect personal data from our customers ("Customers"). Throughout this
      Privacy Policy, "personal information" or "personal data" refers to any
      information that is unique to an individual, such as name, address, email
      address, phone number, IP address and other information that can
      reasonably identify an individual.
    </p>
    <p class="mt-8">
      By using AthleticNext, you are agreeing to the collection and use of your
      personal information for the purposes of providing you our product
      services as described below.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Purpose of processing</strong><br />
      AthleticNext will process and retain your Personal information with
      respect to our Platform for the period necessary to fulfil the Services we
      provide unless a longer retention period is required by law.
    </p>
    <p class="mt-2">
      For statistics retrieval, aggregation and management of web pages and
      services. Information is collected for AthleticNext to get additional
      information about users, including to find out how users access our
      Platform and to track general usage patterns for users on our Platform. We
      use this information to manage and improve our Services.
    </p>

    <p class="mt-8">
      <strong class="font-semibold"
        >What information is being processed? </strong
      ><br />
      You can generally visit our Site without having to submit any personal
      information. If you request more information, or sign up for our Services,
      we will collect personal information as follows.
    </p>
    <p class="mt-2">
      <strong>Account Information.</strong>
      When you register for a AthleticNext customer account (including when you
      join for free) we request your email address and your name. This
      information is your "Account Information" for the purposes of this Privacy
      Policy. Account Information is required to identify you as a Customer and
      permit you to access your account(s). By voluntarily providing us with
      such Account Information, you represent that you are the owner of such
      personal data or otherwise have the requisite consent to provide it to us.
    </p>
    <p class="mt-2">
      <strong>Optional Information.</strong>
      We may also ask you to submit personal information if you choose to use
      interactive features of the Platform, including participation in surveys,
      promotions, requesting customer support, or otherwise communicating with
      us.
    </p>
    <p class="mt-2">
      <strong>Device and Usage Information.</strong>
      When you download, use or interact with the Site, even if you do not have
      an account, we, or authorized third parties engaged by us, may
      automatically collect information about your use of the Site via your
      device, some of which is considered personal information. "Device and
      Usage Information" that we collect consists of:
    </p>
    <div class="mt-2">
      <ul class="ml-10 list-disc">
        <li>
          <strong>Information About your Device:</strong>
          information about the devices and software you use to access the Site
          — primarily the internet browser or mobile device that you use, the
          website or source that linked or referred you to the Site, your IP
          address or device ID (or other persistent identifier that uniquely
          identifies your computer or mobile device on the Internet), the
          operating system of your computer or mobile device, device screen
          size, and other similar technical information.
        </li>
        <li>
          <strong>Usage Information:</strong>
          information about your interactions with the Platform, including
          access dates and times, hardware and software information, device
          event information, log data, crash data, cookie data. This information
          allows us to understand the screens that you view, how you've used the
          Platform (which may include administrative and support communications
          with us), and other actions on the Platforms. We, or authorized third
          parties, automatically collect log data when you access and use the
          Platform, even if you have not created an account or logged in. We use
          this information to administer and improve the Services, analyze
          trends, track users' use of the Platform, and gather broad demographic
          information for aggregate use.
        </li>
        <li>
          <strong>Location Information:</strong>
          we use a third-party service to map the general location to which each
          IP address corresponds ("Location Information"). The Location
          Information that is mapped from an IP address is limited to country
          and city – we do not identify precise location. We use Location
          Information in order to more effectively provide our Services to our
          Customers.
        </li>
        <li>
          <strong>Cookies and Similar Technologies:</strong>
          AthleticNext uses cookies and similar technologies. Through our use of
          cookies (and similar technologies), we or authorized third parties
          collect some Device and Usage Information. Some cookies are necessary
          to make the Site and our content available to you, while others are
          meant to improve the Site and enable us to analyze and measure
          audience and traffic.
        </li>
        <li>
          <strong>Information from Third Parties:</strong>
          in some instances, we process personal information from third parties,
          which consists of data from our partners, such as transactional data
          from providers of payment services, or information from third parties
          with whom we host or sponsor Events. In all such cases, individuals
          are informed of our processing activities and/or able to choose
          certain preferences at the time of collection. From time to time, we
          may combine information we collect as described above with personal
          information we obtain from third parties.
        </li>
      </ul>
    </div>

    <p class="mt-8">
      <strong class="font-semibold">Children's Privacy</strong><br />
      AthleticNext does not knowingly collect information from children under
      the age of 16. If you are under the age of 16, please do not submit any
      personal data through the Platform. We encourage parents and legal
      guardians to monitor their children's Internet usage and to help enforce
      our Privacy Policy by instructing their children never to provide personal
      data on our Platform without their permission.
    </p>
    <p class="mt-2">
      If you have reason to believe that a child under the age of 16 has
      provided personal data to AthleticNext through the Platform, please
      contact support@athleticnext.com and we will endeavour to delete that
      information from our databases.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Access to information</strong><br />
      To ensure that the processing of your information is safe, only authorized
      persons in AthleticNext have access to the information you provide. The
      number of employees with such approval is limited. Access to information
      is secure with several access control mechanisms.
    </p>
    <p class="mt-2">
      We will not disclose, sell, distribute, rent, license, share or forward
      information or personal information to any third party other than those
      with approval and provides services to AthleticNext, unless we have
      permission from you.
    </p>
    <p class="mt-2">
      We will not share personal information with our distribution partners to a
      greater extent than necessary to complete a purpose, such as sending
      brochures and newsletters.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Data Security and Integrity</strong><br />
      We take steps that are reasonably necessary to securely provide our
      Platform. We have put in place reasonably appropriate security measures
      designed to prevent your personal data from being accidentally lost, used
      or accessed in an unauthorized way, altered or disclosed. We limit access
      to personal data only to those employees, agents, contractors and the
      third parties who have a business need-to-know.
    </p>
    <p class="mt-2">
      We also have procedures in place to deal with any suspected data security
      breach. If required, we will notify you and any applicable regulator of a
      suspected data security breach. We also require those parties to whom we
      transfer your personal information to provide acceptable standards of
      security.
    </p>
    <p class="mt-2">
      Notwithstanding, no Internet or email transmission is ever fully secure or
      error free. Email sent to or from the Services may not be secure.
      Therefore, take special care in deciding what information you send to us
      via email. For any questions about the security of your information,
      please contact support@athleticnext.com.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Direct marketing </strong><br />
      Newsletters and information about updates on the platform are broadcasted
      at various times. To unsubscribe from our newsletters please contact
      support@athleticnext.com
    </p>
    <p class="mt-2">
      We do not allow a third party to use your personal information for
      marketing or communication purposes.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Payment Processing </strong><br />
      We do not directly collect your payment information and we do not store
      your payment information. We use third-party, PCI-compliant, payment
      processors, which collect payment information on our behalf in order to
      complete transactions. While our administrators are able to view and track
      actual transactions via customer portals, we do not have access to, or
      process, your credit card information.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Contact Us</strong><br />
      If you have any questions or suggestions regarding this Privacy Policy,
      please contact us at support@athleticnext.com.
    </p>

    <p class="mt-8">
      <strong class="font-semibold">Changes To This Privacy Policy</strong
      ><br />
      This Privacy Policy was last updated on the date indicated below, but we
      suggest that you review it from time to time, as our Platform and our
      business may change. As a result, at times it may be necessary for
      AthleticNext to make changes to this Privacy Policy (and/or our Cookie
      Policy). AthleticNext reserves the right to update or modify this Privacy
      Policy and/or our Cookie Policy at any time and from time to time without
      prior notice. However, if we make changes that we believe will materially
      impact this Privacy Policy, the Cookie Policy or your rights, we will
      promptly notify you of those changes. Your continued use of the Platform
      after any changes or revisions to this Privacy Policy shall indicate your
      agreement with the terms of such revised Privacy Policy.
    </p>

    <p class="mt-8">Last update: October 10, 2021</p>
  </DocumentLayout>
</template>

<script setup lang="ts">
import DocumentLayout from 'src/components/layout/DocumentLayout.vue';
</script>
